import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
// 谷歌登录下线（2026-08-25）— GoogleOAuthProvider 停用，恢复时取消注释
// import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider } from './contexts/ThemeContext';
import { MarketProvider, useMarket } from './contexts/MarketContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { PageTransitionLoader } from './components/UI/LoadingSpinner';
import { isRouteAllowed } from './config/marketFeatures';

// 懒加载页面组件 - 实现页面跳转时的加载动画
const FinabaseHome = lazy(() => import('./pages/FinabaseHome'));
const Login = lazy(() => import('./pages/Login'));
// 注册功能下线（2026-08-25）— Register 页面保留，路由改为重定向 /login
// const Register = lazy(() => import('./pages/Register'));
const WechatCallback = lazy(() => import('./pages/WechatCallback'));
const StockDetail = lazy(() => import('./pages/StockDetail'));
const IndexDetail = lazy(() => import('./pages/IndexDetail'));
const CryptoDetail = lazy(() => import('./pages/CryptoDetail'));
const Settings = lazy(() => import('./pages/Settings'));
const ApiDocs = lazy(() => import('./pages/ApiDocs'));
const DataDocsModule = lazy(() => import('./modules/data-docs'));

// 懒加载模块化组件
const NewsIntelligenceModule = lazy(() => import('./modules/news-intelligence/NewsModule'));
const StrategyMatchingModule = lazy(() => import('./modules/strategy-matching'));
const ShortTermPrediction = lazy(() => import('./modules/short-term-prediction'));
const DataProcessingModule = lazy(() => import('./modules/data-processing'));
const SagaxAgentModule = lazy(() => import('./modules/sagax-agent'));
// MacroDashboard 已并入 F3 因子看板，由 FactorDashboard.js 内部 lazy 加载
const AgenticWall = lazy(() => import('./pages/AgenticWall'));

// 加载中包装组件
const SuspenseWrapper = ({ children }) => (
  <ErrorBoundary>
    <Suspense fallback={<PageTransitionLoader />}>
      {children}
    </Suspense>
  </ErrorBoundary>
);

/**
 * MarketRoute — route guard that redirects to / if the current market
 * doesn't support this path (e.g. /short-term-prediction under crypto).
 */
const MarketRoute = ({ children }) => {
  const { currentMarket } = useMarket();
  const location = useLocation();
  if (!isRouteAllowed(currentMarket.id, location.pathname)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
      <MarketProvider>
      <div className="min-h-screen">
        <AnimatePresence mode="wait">
          <Routes>
            {/* 登录和注册页面 - 不需要Layout */}
            <Route path="/login" element={
              <SuspenseWrapper>
                <Login />
              </SuspenseWrapper>
            } />
            {/* 注册功能下线（2026-08-25）— 旧链接重定向到登录页 */}
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/login/wechat/callback" element={
              <SuspenseWrapper>
                <WechatCallback />
              </SuspenseWrapper>
            } />

            {/* 受保护的路由 - 需要登录 */}
            <Route path="/*" element={
              <ProtectedRoute>
                <Layout>
                  <ErrorBoundary>
                  <Suspense fallback={<PageTransitionLoader />}>
                    <Routes>
                      {/* F1 智能为默认入口：根路径重定向到 sagax-agent；原主页改名「行情」挂到 /overview */}
                      <Route path="/" element={<Navigate to="/sagax-agent" replace />} />
                      <Route path="/overview" element={<MarketRoute><FinabaseHome /></MarketRoute>} />

                      {/* 核心功能模块 */}
                      <Route path="/news-intelligence/*" element={<NewsIntelligenceModule />} />
                      <Route path="/data-processing/*" element={<MarketRoute><DataProcessingModule /></MarketRoute>} />
                      <Route path="/strategy-matching/*" element={<MarketRoute><StrategyMatchingModule /></MarketRoute>} />
                      <Route path="/short-term-prediction/*" element={<MarketRoute><ShortTermPrediction /></MarketRoute>} />
                      <Route path="/sagax-agent/*" element={<SagaxAgentModule />} />

                      {/* 股票详情页面 */}
                      <Route path="/stock-detail/:code" element={<StockDetail />} />

                      {/* 指数详情页面 */}
                      <Route path="/market/index/:code" element={<IndexDetail />} />

                      {/* 加密货币详情页面 */}
                      <Route path="/crypto-detail/:symbol" element={<CryptoDetail />} />

                      {/* 宏观仪表板：已并入 F3 因子看板的 macro tab，旧链接重定向 */}
                      <Route path="/macro" element={<Navigate to="/data-processing/factor?tab=macro" replace />} />

                      {/* F8 任务墙 */}
                      <Route path="/agentic" element={<AgenticWall />} />

                      {/* 设置页面 */}
                      <Route path="/settings" element={<Settings />} />

                      {/* 数据文档模块 (API文档 + SDK文档) */}
                      <Route path="/data-docs/*" element={<DataDocsModule />} />

                      {/* 兼容旧路由 */}
                      <Route path="/api-docs" element={<ApiDocs />} />

                      {/* 404 页面 */}
                      <Route path="*" element={
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <h1 className="text-4xl font-bold text-gray-400 mb-4">404</h1>
                            <p className="text-gray-500 mb-8">页面未找到</p>
                            <button
                              onClick={() => window.location.href = '/'}
                              className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                            >
                              返回首页
                            </button>
                          </div>
                        </div>
                      } />
                    </Routes>
                  </Suspense>
                  </ErrorBoundary>
                </Layout>
              </ProtectedRoute>
            } />
          </Routes>
        </AnimatePresence>
      </div>
      </MarketProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
